/* eslint-disable react-hooks/exhaustive-deps */
import { lazy, useContext, useEffect, useRef, useState } from "react";

/** Common Components */
import CardSection from "../../components/ui/card/CardSection";
import Documents from "../../common/features/documents/Documents";
import ToolbarButtons from "../../components/toolbarButtons/ToolbarButtons";
import RenderTabs2 from "../../components/ui/tabs/RenderTabs2";

/** Configuration Files */
import { productSaleToolbarConfig } from "./productSale.toolbar.Config";

/** CSS Files */
import "./ProductSale.scss";
import ProductSalesFilter from "./ProductSales.filter";
import FormCreator from "../../components/finalForms/FormCreator";
import { productSalesFormConfig } from "./productSales.formfield.config";
import {
  useAddProductSalesMutation,
  useDeleteProductSaleMutation,
  useLazyGetAllInventoryPriceQuery,
  useLazyGetSalesRepQuery,
  useUpdateProductSaleMutation,
} from "../../app/services/productSalesAPI";
import { setDropdownOptions } from "../../components/finalForms/libs/data/mapDropdownOptions";
import ToastService from "../../services/toastService/ToastService";
import { ServiceLocationContext } from "../../utils/context/ServiceLocationContext";
import { getDropdownValue } from "../../utils/formFieldUtils/getDropdownValue";
import CustomCenterModel from "../../components/ui/customCenterModel/CustomCenterModel";
import { AppIcons } from "../../data/appIcons";
import ProductSalesSearch from "./features/productSaleSearch/ProductSalesSearch";
import { useLocation } from "react-router-dom";
import { productSalesShippingAddressField } from "./features/address/config/productSalesMain.formfield.config";
import {
  DocumentCategory,
  DocumentCategoryType,
} from "../../utils/ENUMS/documentCategory";
import SwalAlert from "../../services/swalService/SwalService";
import PropTypes from "prop-types";

/** Lazy Load Components */
const ProductSalesMain = lazy(() => import("./features/main/ProductSalesMain"));
const ProductSalesAddress = lazy(
  () => import("./features/address/ProductSalesAddress"),
);

const ProductSale = ({ props }) => {
  const filterRef = useRef(null);
  const productSalesFormRef = useRef(null);
  const addressRef = useRef();
  const { selectedLocation } = useContext(ServiceLocationContext);

  const location = useLocation();
  const [productSalesFormData, setProductSalesFormData] = useState(
    productSalesFormConfig,
  );
  const [selectedClientKey, setSelectedClientKey] = useState(null);
  const [selectedDepartmentKey, setSelectedDepartmentKey] = useState(null);
  const [lProductSaleKey, setLProductSaleKey] = useState(null);
  const [searchModal, setSearchModal] = useState(false);
  const [invoiceNumberData, setInvoiceNumberData] = useState([]);
  const [addressFormData, setAddressFormData] = useState(
    productSalesShippingAddressField,
  );
  const [invoiceNumber, setInvoiceNumber] = useState(null);

  const locationkey = getDropdownValue(selectedLocation);
  const { confirm } = SwalAlert();

  const [
    getSalesRep,
    {
      isFetching: isGetSalesRepFetching,
      isSuccess: isGetSalesRepFetched,
      data: allGetSalesRepData,
    },
  ] = useLazyGetSalesRepQuery();

  const [
    getAllInventoryPrice,
    {
      isFetching: isGetAllInventoryPriceFetching,
      isSuccess: isGetAllInventoryPriceFetched,
      data: allGetAllInventoryPriceData,
    },
  ] = useLazyGetAllInventoryPriceQuery();

  const [
    addProductSales,
    { isSuccess: isAddProductSalesSuccess, data: addProductSalesData },
  ] = useAddProductSalesMutation();

  const [
    updateProductSales,
    { isSuccess: isUpdateProductSalesSuccess, data: updateProductSalesData },
  ] = useUpdateProductSaleMutation();

  const [
    deleteProductSale,
    {
      isLoading: isDeleteProductSaleLoading,
      isSuccess: isDeleteProductSaleSuccess,
      data: deleteProductSaleData,
    },
  ] = useDeleteProductSaleMutation();

  const handleActionClick = (action) => {
    switch (action) {
      case "SEARCH":
        handleSearchModal();
        break;

      case "SAVE":
        handleSaveClick();
        break;

      case "NEW":
        handleNewClick();
        break;

      case "DELETE":
        handleDeleteClick();
        break;

      default:
        break;
    }
  };

  const handleInvoiceSelect = (invoiceNo) => {
    setInvoiceNumber(invoiceNo);
    setSearchModal(false);
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    const client = searchParams.get("client");
    const department = searchParams.get("department");
    const sales = searchParams.get("sales");

    if (client && department && sales) {
      setSelectedClientKey(Number(client));
      setSelectedDepartmentKey(Number(department));
      setLProductSaleKey(Number(sales));
    }
  }, [location.search]);

  useEffect(() => {
    getSalesRepData();
  }, []);

  const getSalesRepData = () => {
    let req = {
      plSalesRepKey: 0,
      pbIncludeBlank: false,
      pbIncludeAll: false,
      psActiveFlag: "Y",
      plCompanyKey: 0,
      plSalesRepKeyToInclude: 0,
    };
    getSalesRep(req);
  };
  useEffect(() => {
    let req = {
      plInventoryPricingListKey: 0,
      pbIncludeBlank: true,
      pbIncludeInactive: true,
      plInventoryPricingListKeyToInclude: 0,
    };
    getAllInventoryPrice(req);
  }, []);

  const dateFormat = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };
  const handleInvoiceDataLoad = () => {
    if (filterRef.current) {
      filterRef.current.reloadInvoiceDropdown();
    }
  };

  useEffect(() => {
    if (invoiceNumberData?.length > 0) {
      const invoice = invoiceNumberData;

      const selectedFilter = filterRef.current.getSelectedFilters();
      const selectedProductSaleKey = selectedFilter?.invoiceNo?.value;

      const selectedProductSale = invoice.find(
        (item) => item.lProductSaleKey === selectedProductSaleKey,
      );

      if (!selectedProductSale) return;

      // PRODUCT SALES FORM
      const formData = { ...productSalesFormData };

      formData.initialState = {
        ...formData.initialState,
        ...selectedProductSale,
        lSalesRepKey: selectedProductSale?.lSalesRepKey ?? 0,
        lInventoryPricingListKey:
          selectedProductSale?.lInventoryPricingListKey ?? 0,
        dtOrderDate: dateFormat(selectedProductSale?.dtOrderDate),
        dtQuoteDate: dateFormat(selectedProductSale?.dtQuoteDate),
        dtApprovalDate: dateFormat(selectedProductSale?.dtApprovalDate),
        dtInvoiceDate: dateFormat(selectedProductSale?.dtInvoiceDate),
      };

      setProductSalesFormData(formData);

      // ADDRESS FORM
      const addressForm = { ...addressFormData };

      addressForm.initialState = {
        ...addressForm.initialState,

        // Shipping
        sShipCountry: selectedProductSale?.sShipCountry || "",
        sShipName1: selectedProductSale?.sShipName1 || "",
        sShipName2: selectedProductSale?.sShipName2 || "",
        sAddressLine1: selectedProductSale?.sAddressLine1 || "",
        sAddressLine2: selectedProductSale?.sAddressLine2 || "",
        sCity: selectedProductSale?.sCity || "",
        sState: selectedProductSale?.sState || "",
        sZipCode: selectedProductSale?.sZipCode || "",

        // Billing
        sBillCountry: selectedProductSale?.sBillCountry || "",
        sBillName1: selectedProductSale?.sBillName1 || "",
        sBillName2: selectedProductSale?.sBillName2 || "",
        sBillAddressLine1: selectedProductSale?.sBillAddressLine1 || "",
        sBillAddressLine2: selectedProductSale?.sBillAddressLine2 || "",
        sBillCity: selectedProductSale?.sBillCity || "",
        sBillState: selectedProductSale?.sBillState || "",
        sBillZipCode: selectedProductSale?.sBillZipCode || "",
      };

      setAddressFormData(addressForm);
    }
  }, [invoiceNumberData]);

  useEffect(() => {
    if (!isGetSalesRepFetching && isGetSalesRepFetched && allGetSalesRepData) {
      setDropdownOptions(
        allGetSalesRepData,
        "lSalesRepKey",
        "repName",
        productSalesFormData,
        "lSalesRepKey",
        setProductSalesFormData,
      );
    }
  }, [isGetSalesRepFetching, isGetSalesRepFetched, allGetSalesRepData]);

  useEffect(() => {
    if (
      !isGetAllInventoryPriceFetching &&
      isGetAllInventoryPriceFetched &&
      allGetAllInventoryPriceData
    ) {
      setDropdownOptions(
        allGetAllInventoryPriceData,
        "lInventoryPricingListKey",
        "sInventoryPricingList",
        productSalesFormData,
        "lInventoryPricingListKey",
        setProductSalesFormData,
      );
    }
  }, [
    isGetAllInventoryPriceFetching,
    isGetAllInventoryPriceFetched,
    allGetAllInventoryPriceData,
  ]);

  useEffect(() => {
    if (isAddProductSalesSuccess && addProductSalesData) {
      ToastService.success("Product Sales Added Successfully");
      setLProductSaleKey(addProductSalesData?.lProductSaleKey);
      handleInvoiceDataLoad();
    }
  }, [isAddProductSalesSuccess, addProductSalesData]);

  useEffect(() => {
    if (isUpdateProductSalesSuccess && updateProductSalesData) {
      ToastService.success(
        updateProductSalesData.errorMessage ??
          "Product Sales Updated Successfully",
      );
      setLProductSaleKey(updateProductSalesData?.lProductSaleKey);
      getSalesRepData();
    }
  }, [isUpdateProductSalesSuccess, updateProductSalesData]);

  useEffect(() => {
    if (
      !isDeleteProductSaleLoading &&
      isDeleteProductSaleSuccess &&
      deleteProductSaleData
    ) {
      if (deleteProductSaleData) {
        ToastService.success("Product Sales Deleted Successfully");
        handleInvoiceDataLoad();
      }
    }
  }, [
    isDeleteProductSaleLoading,
    isDeleteProductSaleSuccess,
    deleteProductSaleData,
  ]);

  const handleFilterChange = ({ selectedClientKey, selectedDepartmentKey }) => {
    setSelectedClientKey(selectedClientKey);
    setSelectedDepartmentKey(selectedDepartmentKey);
  };

  const handleNewClick = () => {
    let req = {
      plClientKey: selectedClientKey,
      plDepartmentKey: selectedDepartmentKey,
      plServiceLocationKey: locationkey,
    };
    addProductSales(req);
  };

  const handleSearchModal = () => {
    setSearchModal(true);
  };

  const handleSearchModalClose = () => {
    setSearchModal(false);
  };

  const handleSaveClick = () => {
    const mainFormData = productSalesFormRef.current.getProductSalesData();
    const addressData = addressRef.current.getAddressData();

    const request = {
      PlProductSaleKey: invoiceNumber ?? 0,
      PlSalesRepKey: mainFormData.lSalesRepKey ?? 0,

      PdtOrderDate:
        dateFormat(productSalesFormData.initialState.dtOrderDate) ?? null,
      PdtQuoteDate:
        dateFormat(productSalesFormData.initialState.dtQuoteDate) ?? null,
      PdtApprovalDate:
        dateFormat(productSalesFormData.initialState.dtApprovalDate) ?? null,
      PdtInvoiceDate:
        dateFormat(productSalesFormData.initialState.dtInvoiceDate) ?? null,
      PdtCanceledDate:
        dateFormat(productSalesFormData.initialState.dtCanceledDate) ?? null,

      PlContactKey: mainFormData.lContactKey ?? 0,
      PsContactName: mainFormData.contactName ?? "",
      PsContactEmailAddress: mainFormData.sContactEmailAddress,
      PsClientPhoneNumber: mainFormData.sClientPhoneNumber,
      PsPurchaseOrder: mainFormData.sPurchaseOrder,
      PnShippingAmount: mainFormData.nShippingAmount ?? 0,
      nTaxAmount: mainFormData.nTaxAmount ?? 0,
      PnTotalAmount: mainFormData.nTotalAmount ?? 0,
      PsNote: mainFormData.sNote,

      PnQuoteAmount: mainFormData.nQuoteAmount ?? 0,

      PlInventoryPricingListKey: mainFormData.lInventoryPricingListKey ?? 0,
      PsShipTrackingNumber: mainFormData.sShipTrackingNumber,

      PsShipName1: addressData?.sShipName1 ?? "",
      PsShipName2: addressData?.sShipName2 ?? "",
      PsShipAddressLine1: addressData?.sAddressLine1 ?? "",
      PsShipAddressLine2: addressData?.sAddressLine2 ?? "",
      PsShipCity: addressData?.sCity,
      PsShipState:
        addressData?.sState && typeof addressData.sState === "object"
          ? addressData.sState.value
          : addressData?.sState || "",
      PsShipZipCode: addressData?.sZipCode,
      PsShipCountry:
        typeof addressData?.sShipCountry === "object"
          ? addressData?.sShipCountry.value
          : addressData?.sShipCountry,
      PsBillName1: addressData?.sBillName1,
      PsBillName2: addressData?.sBillName2,
      PsBillAddressLine1: addressData?.sBillAddressLine1,
      PsBillAddressLine2: addressData?.sBillAddressLine2,
      PsBillCity: addressData?.sBillCity || "",
      PsBillState:
        addressData?.sBillState && typeof addressData.sBillState === "object"
          ? addressData.sBillState.value
          : addressData?.sBillState || "",
      PsBillZipCode: addressData?.sBillZipCode || "",
      PsBillCountry:
        addressData?.sBillCountry &&
        typeof addressData.sBillCountry === "object"
          ? addressData.sBillCountry.value
          : addressData?.sBillCountry || "",
    };

    updateProductSales(request);
  };

  const handleDeleteClick = () => {
    const plProductSaleKey = invoiceNumber;
    confirm(
      "Are you sure you want to delete this product sales?",
      "Do you wish to continue?",
      "Delete",
      "Cancel",
    ).then((confirmed) => {
      if (confirmed) {
        deleteProductSale(plProductSaleKey);
      }
    });
  };

  const tabs = [
    {
      sMenuItemCaption: "Main",
      component: (
        <ProductSalesMain
          ref={productSalesFormRef}
          clientKey={selectedClientKey}
          departmentKey={selectedDepartmentKey}
          invoiceNumberData={productSalesFormData?.initialState}
        />
      ),
      isDynamic: false,
    },
    {
      sMenuItemCaption: "Address",
      component: (
        <ProductSalesAddress
          ref={addressRef}
          invoiceNumberData={addressFormData?.initialState}
        />
      ),
      isDynamic: false,
    },
    {
      sMenuItemCaption: "Documents",
      component: (
        <Documents
          {...props}
          ownerKey={invoiceNumber}
          DocumentCategoryType={DocumentCategoryType.PAPERWORK_PRODUCT_SALE}
          DocumentCategory={DocumentCategory.PRODUCTSALE}
        />
      ),
      isDynamic: false,
    },
  ];

  return (
    <div className="product-sale-section">
      <div className="row justify-content-center">
        <div className="col-12">
          <ToolbarButtons
            toolbarConfiguration={productSaleToolbarConfig}
            onActionClick={handleActionClick}
          />
        </div>
        <div className="col-md-12">
          <div className="top-main-filters form-card px-2">
            <ProductSalesFilter
              ref={filterRef}
              onFilterChange={handleFilterChange}
              lProductSaleKey={lProductSaleKey}
              selectedClientKey={selectedClientKey}
              selectedDepartmentKey={selectedDepartmentKey}
              onInvoiceDataLoad={setInvoiceNumberData}
              onInvoiceChange={setInvoiceNumber}
            />
          </div>
        </div>

        <div className="col-12">
          <CardSection>
            <FormCreator
              ref={productSalesFormRef}
              config={productSalesFormData}
            />
          </CardSection>
        </div>
        <div className="product-sale-description">
          <RenderTabs2 tabs={tabs} />
        </div>
      </div>
      <CustomCenterModel
        isOpen={searchModal}
        modalTitle={"Product Sales Search"}
        onClose={handleSearchModalClose}
        modalTitleIcon={AppIcons.searchIcon}
        contentClass="content-65"
      >
        <ProductSalesSearch
          onSelectInvoice={handleInvoiceSelect}
          setSearchModal={setSearchModal}
        />
      </CustomCenterModel>
    </div>
  );
};

ProductSale.propTypes = {
  props: PropTypes.shape({
    clientKey: PropTypes.number,
    departmentKey: PropTypes.number,
    invoiceNumberData: PropTypes.array,
    lProductSaleKey: PropTypes.number,
    selectedClientKey: PropTypes.number,
    selectedDepartmentKey: PropTypes.number,
  }),
};
export default ProductSale;
